/* =========================================================================
   ClueVine — Site admin (owner tools)
   A soft-gated, in-app control panel for the live site: toggle features/modes,
   manage the one ad + support/tip links at runtime, and a marketing dashboard
   (every artifact, paste-ready copy, and the go-to-market plan). Everything is
   client-side config persisted locally — a clean seam to back with MySQL later.
   The passphrase is obscurity, not security: gate /admin on your server for real.
   ========================================================================= */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '../components/Button';
import { SegmentedControl } from '../components/SegmentedControl';
import { Switch } from '../components/Switch';
import { useToast } from '../components/Toast';
import { ADMIN_PASSPHRASE } from '../config';
import {
  MARKETING_ASSETS,
  MARKETING_COPY,
  MARKETING_PLAN,
  MODE_CLIP_IDS,
} from '../data/marketing';
import { allModes } from '../engine/registry';
import { useNav } from '../state/NavContext';
import { loadSlice, saveSlice, type SliceConfig } from '../state/persistence';
import { useSiteConfig } from '../state/SiteConfigContext';
import { useSupporter } from '../state/SupporterContext';
import { copyText } from '../utils/share';

const unlockSlice: SliceConfig<boolean> = { name: 'admin-unlocked', version: 1, defaults: () => false };

type Tab = 'features' | 'monetization' | 'marketing';

export function AdminScreen() {
  const [unlocked, setUnlocked] = useState(() => loadSlice(unlockSlice));
  const [tab, setTab] = useState<Tab>('features');
  const { navigate } = useNav();

  if (!unlocked) {
    return (
      <AdminGate
        onUnlock={() => {
          saveSlice(unlockSlice, true);
          setUnlocked(true);
        }}
      />
    );
  }

  return (
    <div className="cv-admin">
      <div className="cv-page-head">
        <span className="cv-eyebrow">Owner tools</span>
        <h1>Site admin</h1>
      </div>
      <p className="cv-admin__note">
        Changes save to this browser and take effect live. This is client-side config — when you add
        MySQL, sync it from your server. The passphrase is obscurity, not security.
      </p>

      <SegmentedControl<Tab>
        block
        ariaLabel="Admin section"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'features', label: 'Features' },
          { value: 'monetization', label: 'Monetization' },
          { value: 'marketing', label: 'Marketing' },
        ]}
      />

      <div className="cv-admin__panel">
        {tab === 'features' ? <FeaturesTab /> : null}
        {tab === 'monetization' ? <MonetizationTab /> : null}
        {tab === 'marketing' ? <MarketingTab /> : null}
      </div>

      <Button variant="ghost" block iconLeft="home" onClick={() => navigate('home')}>
        Back to site
      </Button>
    </div>
  );
}

function AdminGate({ onUnlock }: { onUnlock: () => void }) {
  const { navigate } = useNav();
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (value === ADMIN_PASSPHRASE) onUnlock();
    else setError(true);
  };

  return (
    <div className="cv-admin">
      <div className="cv-page-head">
        <span className="cv-eyebrow">Restricted</span>
        <h1>Site admin</h1>
      </div>
      <form onSubmit={submit} className="cv-stack" style={{ maxWidth: '22rem' }}>
        <div className="cv-field">
          <label className="cv-label" htmlFor="cv-admin-pass">
            Passphrase
          </label>
          <input
            id="cv-admin-pass"
            className="cv-input"
            type="password"
            value={value}
            autoFocus
            onChange={(e) => {
              setValue(e.target.value);
              setError(false);
            }}
            placeholder="Enter the admin passphrase"
          />
          {error ? (
            <span className="cv-help" style={{ color: 'var(--danger)' }}>
              Not quite — it’s ADMIN_PASSPHRASE in src/config.ts.
            </span>
          ) : null}
        </div>
        <Button variant="primary" block type="submit">
          Unlock
        </Button>
        <Button variant="ghost" block onClick={() => navigate('home')}>
          Back to site
        </Button>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="cv-admin__section">
      <h2 className="cv-admin__h2">{title}</h2>
      {children}
    </section>
  );
}

function Row({ title, desc, children }: { title: string; desc?: string; children: ReactNode }) {
  return (
    <div className="cv-setting-row">
      <div className="cv-setting-row__text">
        <span className="cv-setting-row__title">{title}</span>
        {desc ? <span className="cv-setting-row__desc">{desc}</span> : null}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="cv-field">
      <span className="cv-label">{label}</span>
      <input
        className="cv-input"
        value={value}
        placeholder={placeholder}
        spellCheck={false}
        autoCapitalize="off"
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function FeaturesTab() {
  const { config, update, isModeEnabled, setModeEnabled, reset } = useSiteConfig();
  const modes = allModes();
  const enabled = modes.filter((m) => isModeEnabled(m.id)).length;

  return (
    <div className="cv-stack">
      <Section title="Home">
        <Row title="Mode of the day" desc="Feature one mode on Home each day.">
          <Switch
            label="Mode of the day"
            checked={config.showModeOfDay}
            onChange={(v) => update({ showModeOfDay: v })}
          />
        </Row>
      </Section>

      <Section title={`Modes — ${enabled}/${modes.length} enabled`}>
        <p className="cv-help">
          Turn a mode off to hide it from Home, the how-to guide, and the mode-of-the-day pick.
          These switches apply to this device only; set them site-wide in src/config.ts.
        </p>
        <div className="cv-admin__modes">
          {modes.map((m) => (
            <Row key={m.id} title={m.name}>
              <Switch
                label={m.name}
                checked={isModeEnabled(m.id)}
                onChange={(v) => setModeEnabled(m.id, v)}
              />
            </Row>
          ))}
        </div>
      </Section>

      <Button variant="ghost" onClick={reset}>
        Reset site config to defaults
      </Button>
    </div>
  );
}

function MonetizationTab() {
  const { config, update } = useSiteConfig();
  const { isSupporter, grant, revoke } = useSupporter();

  return (
    <div className="cv-stack">
      <Section title="The one ad">
        <Row title="Show the ad" desc="A single banner for non-supporters. Leave off until you have traffic.">
          <Switch label="Show the ad" checked={config.adsEnabled} onChange={(v) => update({ adsEnabled: v })} />
        </Row>
        <Field label="AdSense client (ca-pub-…)" value={config.adsClient} placeholder="ca-pub-…" onChange={(v) => update({ adsClient: v })} />
        <Field label="AdSense slot id" value={config.adsSlot} placeholder="1234567890" onChange={(v) => update({ adsSlot: v })} />
        <p className="cv-help">
          The ad renders only when it’s on AND both fields are set. AdSense serves on an approved
          domain, never on localhost.
        </p>
      </Section>

      <Section title="Support & tips">
        <Field label="Stripe Payment Link (Supporter)" value={config.supportUrl} placeholder="https://buy.stripe.com/…" onChange={(v) => update({ supportUrl: v })} />
        <Field label="Ko-fi / Buy Me a Coffee link" value={config.tipUrl} placeholder="https://ko-fi.com/you" onChange={(v) => update({ tipUrl: v })} />
        <p className="cv-help">
          Set the Stripe link’s after-payment redirect to
          <code> https://cluevine.com/?supporter=ok </code> to flip the supporter flag on return.
        </p>
      </Section>

      <Section title="Supporter (this device)">
        <p className="cv-help">
          Status: {isSupporter ? '🌱 Supporter — ad removed, skins unlocked' : 'Not a supporter'}. Toggle
          to preview both states.
        </p>
        <div className="cv-row" style={{ gap: 'var(--space-xs)' }}>
          <Button variant="secondary" onClick={grant} disabled={isSupporter}>
            Grant
          </Button>
          <Button variant="ghost" onClick={revoke} disabled={!isSupporter}>
            Revoke
          </Button>
        </div>
        <p className="cv-help">Honor-system flag, per device. Real cross-user enforcement needs your server.</p>
      </Section>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const toast = useToast();
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={async () => {
        const ok = await copyText(text);
        toast.show(ok ? 'Copied to clipboard' : 'Could not copy', ok ? 'success' : 'error');
      }}
    >
      Copy
    </Button>
  );
}

function MarketingTab() {
  return (
    <div className="cv-stack">
      <Section title="Assets">
        <div className="cv-admin__assets">
          {MARKETING_ASSETS.map((a) => (
            <figure key={a.src} className="cv-admin__asset">
              <img src={a.src} alt={a.name} loading="lazy" />
              <figcaption>
                <strong>{a.name}</strong>
                <span className="cv-help">{a.use}</span>
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="cv-help">
          Plus {MODE_CLIP_IDS.length} per-mode clips at <code>/marketing/modes/&lt;mode&gt;.gif</code> and
          the README gallery.
        </p>
      </Section>

      <Section title="Paste-ready copy">
        <div className="cv-stack">
          {MARKETING_COPY.map((c) => (
            <div key={c.label} className="cv-admin__copy">
              <div className="cv-row-between">
                <span className="cv-eyebrow">{c.label}</span>
                <CopyButton text={c.text} />
              </div>
              <pre className="cv-admin__pre">{c.text}</pre>
            </div>
          ))}
        </div>
      </Section>

      <Section title="The plan">
        {MARKETING_PLAN.map((s) => (
          <div key={s.phase} className="cv-admin__plan">
            <div className="cv-row" style={{ gap: 'var(--space-xs)', alignItems: 'baseline' }}>
              <span className="cv-chip cv-chip--seed">{s.phase}</span>
              <strong>{s.title}</strong>
            </div>
            <ul className="cv-admin__plan-list">
              {s.points.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        ))}
      </Section>
    </div>
  );
}
